/**
 * HeaderActions — the app bar's right-hand cluster.
 *
 * Port of the legacy Headerbar's action group: Feedback, Demo, Notifications,
 * Support, and the user menu (Profile → Common Services, Online
 * Documentation → the current page's guide, About, Logout). Split
 * out of AppBar so the bar itself stays a layout file.
 *
 * Signed out — which only happens on the Advisory Home page, the one page the
 * shell shows before sign-in — the cluster is legacy's signed-out set:
 * Feedback, Demo and Support. Notifications and the profile menu need a user,
 * so they appear only once there is one (legacy gates them on `username`).
 *
 * Differences from legacy, all deliberate:
 *  - Notifications poll nothing. Legacy polls every 15s via a timer whose
 *    cleanup sits in a Vue 2 hook that never fires in Vue 3 — a documented
 *    leak. When the endpoint is live this becomes an RTK Query subscription,
 *    which handles its own teardown.
 *  - Advisory Bot is not here: it renders only outside production and points at
 *    a hard-coded dev host. Its URL is in config, ready to add.
 */
import { useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import {
  FEEDBACK_URL,
  META_COLLECTOR_URL,
  PROFILE_MENU,
  PROFILE_URL,
  DEMO_URLS,
} from '@config/headerActions';
import { useAppMode } from '@variant/useAppMode';
import { AboutDialog } from '@components/common/AboutDialog';
import {
  AccountIcon,
  FeedbackIcon,
  HeadsetIcon,
  OpenInNewIcon,
  PlayIcon,
} from '@components/common/icons';
import { OnlineDocsDialog, helpTopicFor } from '@components/common/OnlineDocs';
import { Button, Menu, Modal, Tooltip } from '@components/ui';
import { useNotify } from '@hooks/useNotify';
import { getBasename } from '@router/basename';
import { selectIsAuthenticated, selectUser } from '@security/auth/authSlice';
import { buildSsoUrl, safeHttpUrl, useSignOut } from '@features/auth';
import { NotificationsMenu } from '@features/notifications';
import { VariantGate } from '@variant/VariantGate';

import styles from './HeaderActions.module.css';

/** Icon-only action with a tooltip, used by every control except Feedback. */
function IconAction({ id, label, onClick, children }) {
  return (
    <Tooltip label={label} side="bottom">
      <button
        type="button"
        id={id}
        className={styles.iconButton}
        aria-label={label}
        onClick={onClick}
      >
        {children}
      </button>
    </Tooltip>
  );
}

export function HeaderActions() {
  const { mode } = useAppMode();
  const [demoOpen, setDemoOpen] = useState(false);
  // 'about' | 'docs' | null — the profile menu's dialogs.
  const [dialog, setDialog] = useState(null);
  const signOut = useSignOut();
  const notify = useNotify();
  const user = useSelector(selectUser);
  const isAuthed = useSelector(selectIsAuthenticated);
  const location = useLocation();
  // The guide for the page being shown, or null (then the item is left out).
  const helpTopic = helpTopicFor({ pathname: location.pathname, search: location.search, mode });

  // The signed-in user. Legacy renders the `username` cookie raw, which is why
  // it looks like an email — it is one. No fallback: the profile menu renders
  // only when signed in, so there is always a real user to name.
  const displayName = user?.email ?? '';

  /*
   * Support opens in a NEW TAB.
   *
   * The path has to carry whatever basename the router mounted with: '/cca' or
   * '/eia' locally, none on a deployed host. React Router adds it for in-app
   * navigation — but window.open goes straight to the browser, so locally a
   * bare '/support' would land outside the basename and match nothing.
   * getBasename() is the same source the router itself uses, so the two cannot
   * drift, and it yields '' (plain '/support') where there is no prefix.
   */
  const openSupport = () =>
    window.open(`${getBasename(mode)}/support`, '_blank', 'noopener,noreferrer');

  /*
   * Profile leaves for Common Services' request-role page in the SAME tab, as
   * legacy (window.location.href). buildSsoUrl adds `?appName=` and refuses
   * anything but http(s), so a bad env value cannot become a navigation.
   */
  const openProfile = () => {
    const url = buildSsoUrl(PROFILE_URL, mode.toUpperCase());
    if (url) window.location.assign(url);
    else notify.error(null, 'Profile is not available in this environment.');
  };

  const PROFILE_ACTIONS = {
    profile: openProfile,
    docs: () => setDialog('docs'),
    about: () => setDialog('about'),
    logout: signOut,
  };

  const profileItems = PROFILE_MENU.filter((item) => item.id !== 'docs' || helpTopic).map(
    (item) => ({ ...item, onSelect: PROFILE_ACTIONS[item.id] }),
  );

  return (
    <div className={styles.actions}>
      {/*
        Meta Collector — EIA only, and only once signed in.

        Legacy renders it on `username && !application`, where `application` is
        `isCCA() ? true : false` (Headerbar.vue:32, :396-398) — a double negative
        that means "EIA". VariantGate says the same thing in one readable word,
        which is what .claude/rules/variants.md asks for instead of a raw mode
        comparison in JSX.

        The sign-in condition is legacy's too: the tool is useless to an
        anonymous visitor, so the button would only lead them somewhere they
        cannot act.
      */}
      <VariantGate only="eia">
        {isAuthed ? (
          <Button
            id="btn-header-metacollector"
            // secondary-solid, not secondary: the plain library secondary is
            // neutral (white label, faint white edge). This is the app's teal
            // outline-on-black — see components/ui/Button/Button.module.css.
            variant="secondary-solid"
            size="sm"
            className={styles.feedbackButton}
            endIcon={<OpenInNewIcon />}
            // noopener/noreferrer, which legacy omits at Headerbar.vue:535 —
            // without them the opened page gets a handle on this one via
            // window.opener and can navigate it away (reverse tabnabbing).
            onClick={() => {
              // VITE_METACOLLECTOR_URL only; http(s) only (safeHttpUrl).
              const url = safeHttpUrl(META_COLLECTOR_URL);
              if (url) window.open(url, '_blank', 'noopener,noreferrer');
              else notify.error(null, 'Meta Collector is not available in this environment.');
            }}
          >
            Meta Collector
          </Button>
        ) : null}
      </VariantGate>

      <Button
        id="btn-header-feedback"
        variant="secondary"
        size="sm"
        className={styles.feedbackButton}
        startIcon={<FeedbackIcon />}
        onClick={() => window.open(FEEDBACK_URL, '_blank', 'noopener,noreferrer')}
      >
        Feedback
      </Button>

      <IconAction id="btn-header-demo" label="Demo" onClick={() => setDemoOpen(true)}>
        <PlayIcon />
      </IconAction>

      {/* A bell with nobody's notifications behind it would only ever be empty. */}
      {isAuthed ? <NotificationsMenu /> : null}

      {/* The headset is the one way to the support page (legacy desktop has no
          Support in the profile menu). It opens a new tab, so whatever the user
          was doing when they needed help is still there behind it. */}
      <IconAction id="btn-header-support" label="Support" onClick={openSupport}>
        <HeadsetIcon />
      </IconAction>

      {isAuthed ? (
        <Menu
          align="right"
          items={profileItems}
          trigger={
            <button type="button" id="btn-header-profile" className={styles.user}>
              <span className={styles.userIcon} aria-hidden="true">
                <AccountIcon />
              </span>
              <span className={styles.userName}>{displayName}</span>
            </button>
          }
        />
      ) : null}

      {/*
        Near-full-screen iframe, as legacy does — the demo is a hosted product
        tour, not a page of this app. The wrapper exists only so the dialog can
        be sized; see .demoModal in the stylesheet for why that is necessary.
      */}
      <div className={styles.demoModal}>
        <Modal open={demoOpen} onClose={() => setDemoOpen(false)} title="Demo">
          <iframe className={styles.demoFrame} title="Demo" src={DEMO_URLS[mode]} allowFullScreen />
        </Modal>
      </div>

      {dialog === 'about' ? <AboutDialog onClose={() => setDialog(null)} /> : null}
      {/* `helpTopic` re-checked: the page may have changed while it was open. */}
      {dialog === 'docs' && helpTopic ? (
        <OnlineDocsDialog topic={helpTopic} onClose={() => setDialog(null)} />
      ) : null}
    </div>
  );
}

export default HeaderActions;
